import { useState } from 'react';

import {
  useApi,
  identityApiRef,
  discoveryApiRef,
  alertApiRef,
  DiscoveryApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { OwnershipEntity, OwnershipHandlerResult } from '../types';

interface UserProfile {
  email?: string;
}

export interface SubscriptionOwnershipRequest {
  subscription_id: string;
  team_id: string;
  team_name: string;
  email: string;
}

export interface SubscriptionOwnershipResponse {
  success?: boolean;
  message?: string;
  [key: string]: unknown;
}

async function updateSubscriptionOwnership(
  discoveryApi: DiscoveryApi,
  request: SubscriptionOwnershipRequest,
): Promise<SubscriptionOwnershipResponse> {
  const baseUrl = await discoveryApi.getBaseUrl('watchtower');

  const response = await fetch(`${baseUrl}/subscriptions/ownership`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to update subscription ownership: ${response.status} ${response.statusText}. ${errorText}`,
    );
  }

  return response.json();
}

async function refreshSubscriptions(
  discoveryApi: DiscoveryApi,
  subscriptionIds: string[],
): Promise<{ updatedCount: number }> {
  const baseUrl = await discoveryApi.getBaseUrl('utility-api');

  const response = await fetch(
    `${baseUrl}/actions/refresh-subscriptions-ownership`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ subscription_ids: subscriptionIds }),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to refresh subscriptions: ${response.status} ${response.statusText}. ${errorText}`,
    );
  }

  return response.json();
}

export function useSubscriptionOwnershipHandler(): OwnershipHandlerResult {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const alertApi = useApi(alertApiRef);
  const [loading, setLoading] = useState(false);

  const failWithAlert = (message: string): false => {
    alertApi.post({ message, severity: 'error' });
    return false;
  };

  const submit = async (
    entities: OwnershipEntity[],
    ownerRef: string,
  ): Promise<boolean> => {
    setLoading(true);
    try {
      const fullTeamEntityRef = ownerRef.includes(':')
        ? ownerRef
        : `group:default/${ownerRef}`;

      const teamEntity = await catalogApi.getEntityByRef(fullTeamEntityRef);
      if (!teamEntity) {
        return failWithAlert(`Team entity not found: ${fullTeamEntityRef}`);
      }

      const teamId = teamEntity.metadata.name;
      const teamName = teamEntity.metadata.title || teamEntity.metadata.name;

      const identity = await identityApi.getBackstageIdentity();
      const userEntity = await catalogApi.getEntityByRef(
        identity.userEntityRef,
      );
      const userEmail = (
        userEntity?.spec?.profile as UserProfile
      )?.email?.trim();
      if (!userEmail) {
        return failWithAlert(
          'Could not determine your email from your user profile. Please contact us via Slack #sig-frontstage.',
        );
      }

      const subscriptionIds: string[] = [];

      for (const entity of entities) {
        const subscriptionId =
          entity.annotations?.['tomtom.com/subscription-id'];

        if (!subscriptionId) {
          return failWithAlert(
            `Subscription "${entity.name}" is missing tomtom.com/subscription-id annotation`,
          );
        }

        subscriptionIds.push(subscriptionId);

        const updateResult = await updateSubscriptionOwnership(discoveryApi, {
          subscription_id: subscriptionId,
          team_id: teamId.toUpperCase(),
          team_name: teamName,
          email: userEmail,
        });

        if (updateResult.success === false) {
          return failWithAlert(
            updateResult.message ||
              `Failed to update ownership for subscription ${subscriptionId}`,
          );
        }
      }

      await refreshSubscriptions(discoveryApi, subscriptionIds);

      const message =
        entities.length === 1
          ? `Successfully assigned ownership of subscription to ${teamName}`
          : `Successfully assigned ownership for ${entities.length} subscriptions to ${teamName}`;

      alertApi.post({
        message,
        severity: 'success',
        display: 'transient',
      });

      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      return failWithAlert(
        `Failed to update subscription ownership: ${errorMessage}`,
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    submit,
    loading,
  };
}
